
import React, { useCallback, useEffect, useState } from "react";
import {
    Accordion,
    Alert,
    Badge,
    Box,
    Button,
    Divider,
    Group,
    Loader,
    Paper,
    Stack,
    Text,
    Textarea,
    TextInput,
    ThemeIcon,
    Title,
} from "@mantine/core";
import {
    IconAlertCircle,
    IconBuildingBank,
    IconCheck,
    IconEye,
    IconLock,
    IconRefresh,
    IconSend,
} from "@tabler/icons-react";

type Props = {
    eventId: number;
    personId: number;
    apiUrl: string;
};

type PaymentForm = {
    beneficiaryName: string;
    beneficiaryAddress: string;
    email: string;
    bankName: string;
    bankAddress: string;
    accountNumber: string;
    iban: string;
    bicSwiftAddress: string;
    comment: string;
};

type PaymentExistsResponse = {
    exists: boolean;
    status?: string | null;
    createdAt?: string | null;
};

type PaymentDetails = PaymentForm & {
    status: string;
    createdAt: string;
    paidAt?: string | null;
};

type PageState =
    | "loading"
    | "form"
    | "submitted"
    | "details"
    | "error";

const emptyForm: PaymentForm = {
    beneficiaryName: "",
    beneficiaryAddress: "",
    email: "",
    bankName: "",
    bankAddress: "",
    accountNumber: "",
    iban: "",
    bicSwiftAddress: "",
    comment: "",
};

const fieldLabels: Record<keyof PaymentForm, string> = {
    beneficiaryName: "Beneficiary name",
    beneficiaryAddress: "Beneficiary address",
    email: "Email address",
    bankName: "Bank name",
    bankAddress: "Bank address",
    accountNumber: "Account number",
    iban: "IBAN",
    bicSwiftAddress: "BIC / SWIFT",
    comment: "Comment",
};

const requiredFields: (keyof PaymentForm)[] = [
    "beneficiaryName",
    "beneficiaryAddress",
    "email",
    "bankName",
    "bankAddress",
    "accountNumber",
    "iban",
    "bicSwiftAddress",
];

function formatDate(value?: string | null): string {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getStatusColor(status?: string | null): string {
    switch ((status ?? "").toLowerCase()) {
        case "paid":
        case "completed":
        case "approved":
            return "green";

        case "rejected":
        case "cancelled":
            return "red";

        default:
            return "yellow";
    }
}

async function getApiError(response: Response): Promise<string> {
    try {
        const data = await response.json();

        return (
            data.message ||
            data.title ||
            `Request failed (${response.status}).`
        );
    } catch {
        return `Request failed (${response.status}).`;
    }
}

export default function PaymentSection({
    eventId,
    personId,
    apiUrl,
}: Props) {
    const [pageState, setPageState] =
        useState<PageState>("loading");

    const [form, setForm] = useState<PaymentForm>({
        ...emptyForm,
    });

    const [errors, setErrors] = useState<
        Partial<Record<keyof PaymentForm, string>>
    >({});

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");

    const [existing, setExisting] =
        useState<PaymentExistsResponse | null>(null);

    const [details, setDetails] =
        useState<PaymentDetails | null>(null);

    const [submitting, setSubmitting] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);

    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const baseUrl =
        `${apiUrl.replace(/\/+$/, "")}` +
        `/api/payment-requests/events/${eventId}/persons/${personId}`;

    const loadExisting = useCallback(async () => {
        setPageState("loading");
        setError("");
        setDetails(null);

        try {
            const response = await fetch(
                `${baseUrl}/exists`,
                {
                    method: "GET",
                    //credentials: "include",
                    cache: "no-store",
                }
            );

            if (!response.ok) {
                throw new Error(await getApiError(response));
            }

            const data: PaymentExistsResponse =
                await response.json();

            setExisting(data);

            if (data.exists) {
                setPageState("submitted");
            } else {
                setPageState("form");
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Could not load allowance request."
            );

            setPageState("error");
        }
    }, [baseUrl]);

    useEffect(() => {
        void loadExisting();
    }, [loadExisting]);

    function updateField(
        field: keyof PaymentForm,
        value: string
    ) {
        setForm((previous) => ({
            ...previous,
            [field]: value,
        }));

        setErrors((previous) => ({
            ...previous,
            [field]: undefined,
        }));
    }

    function validateForm(): boolean {
        const nextErrors: Partial<
            Record<keyof PaymentForm, string>
        > = {};

        requiredFields.forEach((field) => {
            if (!form[field].trim()) {
                nextErrors[field] = "This field is required.";
            }
        });

        if (
            form.email.trim() &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
        ) {
            nextErrors.email = "Enter a valid email address.";
        }

        const normalizedIban = form.iban
            .replace(/\s/g, "")
            .toUpperCase();

        if (
            normalizedIban &&
            !/^[A-Z]{2}[0-9A-Z]{13,32}$/.test(normalizedIban)
        ) {
            nextErrors.iban = "Enter a valid IBAN format.";
        }

        const normalizedBic = form.bicSwiftAddress
            .replace(/\s/g, "")
            .toUpperCase();

        if (
            normalizedBic &&
            !/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(normalizedBic)
        ) {
            nextErrors.bicSwiftAddress =
                "BIC / SWIFT must contain 8 or 11 characters.";
        }

        setErrors(nextErrors);

        return Object.keys(nextErrors).length === 0;
    }

    async function submitForm(
        event: React.FormEvent<HTMLFormElement>
    ) {
        event.preventDefault();

        if (submitting || !validateForm()) {
            return;
        }

        setSubmitting(true);
        setError("");
        setSuccessMessage("");

        try {
            const payload: PaymentForm = {
                ...form,
                beneficiaryName: form.beneficiaryName.trim(),
                beneficiaryAddress:
                    form.beneficiaryAddress.trim(),
                email: form.email.trim(),
                bankName: form.bankName.trim(),
                bankAddress: form.bankAddress.trim(),
                accountNumber: form.accountNumber.trim(),
                iban: form.iban
                    .replace(/\s/g, "")
                    .toUpperCase(),
                bicSwiftAddress: form.bicSwiftAddress
                    .replace(/\s/g, "")
                    .toUpperCase(),
                comment: form.comment.trim(),
            };

            const response = await fetch(baseUrl, {
                method: "POST",
                //credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (response.status === 409) {
                setSuccessMessage(
                    "An allowance request has already been submitted."
                );

                await loadExisting();
                return;
            }

            if (!response.ok) {
                throw new Error(await getApiError(response));
            }

            setEmail("");
            setForm({ ...emptyForm });
            setErrors({});
            setDetails(null);

            setSuccessMessage(
                "Your allowance request has been submitted successfully."
            );

            await loadExisting();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Could not submit allowance request."
            );
        } finally {
            setSubmitting(false);
        }
    }

    async function viewDetails(
        event: React.FormEvent<HTMLFormElement>
    ) {
        event.preventDefault();

        if (checkingEmail) return;

        setEmailError("");
        setError("");

        if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                email.trim()
            )
        ) {
            setEmailError("Enter a valid email address.");
            return;
        }

        setCheckingEmail(true);

        try {
            const response = await fetch(
                `${baseUrl}/view`,
                {
                    method: "POST",
                    //credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        email: email.trim(),
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(await getApiError(response));
            }

            const data: PaymentDetails =
                await response.json();

            setDetails(data);
            setPageState("details");
            setEmail("");
        } catch (err) {
            setEmailError(
                err instanceof Error
                    ? err.message
                    : "Could not verify email address."
            );
        } finally {
            setCheckingEmail(false);
        }
    }

    function renderField(
        field: keyof PaymentForm,
        options?: {
            textarea?: boolean;
            required?: boolean;
            placeholder?: string;
        }
    ) {
        const common = {
            label: fieldLabels[field],
            value: form[field],
            onChange: (
                event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                >
            ) => updateField(field, event.currentTarget.value),
            error: errors[field],
            required: options?.required ?? true,
            placeholder: options?.placeholder,
            disabled: submitting,
        };

        if (options?.textarea) {
            return (
                <Textarea
                    key={field}
                    {...common}
                    minRows={3}
                    autosize
                />
            );
        }

        return (
            <TextInput
                key={field}
                {...common}
                type={field === "email" ? "email" : "text"}
            />
        );
    }

    function renderStatus() {
        const status =
            details?.status ?? existing?.status ?? "Pending";

        return (
            <Badge
                color={getStatusColor(status)}
                variant="light"
                size="lg"
            >
                {status}
            </Badge>
        );
    }

    function renderDetailsRow(
        label: string,
        value?: string | null
    ) {
        return (
            <Box key={label}>
                <Text size="xs" c="dimmed" fw={600}>
                    {label}
                </Text>

                <Text size="sm" fw={500} style={{
                    overflowWrap: "anywhere",
                    whiteSpace: "pre-wrap",
                }}>
                    {value || "-"}
                </Text>
            </Box>
        );
    }

    return (
        <Accordion.Item value="payment">
            <Accordion.Control
                icon={
                    <ThemeIcon
                        color="red"
                        variant="light"
                        radius="xl"
                    >
                        <IconBuildingBank size={18} />
                    </ThemeIcon>
                }
            >
                <Group gap="sm" wrap="wrap">
                    <Text fw={600}>Allowance Request</Text>

                    {existing?.exists && renderStatus()}
                </Group>
            </Accordion.Control>

            <Accordion.Panel>
                <Stack gap="md">
                    {successMessage && (
                        <Alert
                            color="green"
                            icon={<IconCheck size={18} />}
                            title="Success"
                        >
                            {successMessage}
                        </Alert>
                    )}

                    {error && (
                        <Alert
                            color="red"
                            icon={<IconAlertCircle size={18} />}
                            title="Error"
                        >
                            {error}
                        </Alert>
                    )}

                    {pageState === "loading" && (
                        <Group justify="center" py="xl">
                            <Loader color="red" size="sm" />
                            <Text size="sm" c="dimmed">
                                Loading allowance request...
                            </Text>
                        </Group>
                    )}

                    {pageState === "error" && (
                        <Button
                            variant="light"
                            color="red"
                            leftSection={
                                <IconRefresh size={16} />
                            }
                            onClick={() => void loadExisting()}
                        >
                            Try again
                        </Button>
                    )}

                    {pageState === "form" && (
                        <Paper
                            withBorder
                            radius="md"
                            p="md"
                        >
                            <form onSubmit={submitForm}>
                                <Stack gap="md">
                                    <Box>
                                        <Title order={4}>
                                            Allowance Request
                                        </Title>

                                        <Text
                                            size="sm"
                                            c="dimmed"
                                            mt={4}
                                        >
                                            Please enter your personal
                                            and banking details.
                                            You can only submit one
                                            request per event.
                                        </Text>
                                    </Box>

                                    <Divider
                                        label="Beneficiary information"
                                        labelPosition="left"
                                    />

                                    {renderField(
                                        "beneficiaryName"
                                    )}

                                    {renderField(
                                        "beneficiaryAddress",
                                        { textarea: true }
                                    )}

                                    {renderField("email", {
                                        placeholder:
                                            "name@example.com",
                                    })}

                                    <Divider
                                        label="Bank information"
                                        labelPosition="left"
                                    />

                                    {renderField("bankName")}

                                    {renderField(
                                        "bankAddress",
                                        { textarea: true }
                                    )}

                                    {renderField(
                                        "accountNumber"
                                    )}

                                    {renderField("iban", {
                                        placeholder:
                                            "DK5000400440116243",
                                    })}

                                    {renderField(
                                        "bicSwiftAddress",
                                        {
                                            placeholder:
                                                "BANKDKKK",
                                        }
                                    )}

                                    <Divider
                                        label="Additional information"
                                        labelPosition="left"
                                    />

                                    {renderField("comment", {
                                        textarea: true,
                                        required: false,
                                    })}

                                    <Alert
                                        color="blue"
                                        variant="light"
                                        icon={
                                            <IconLock size={18} />
                                        }
                                    >
                                        Please check all details
                                        carefully before submitting.
                                        Once submitted, your request
                                        cannot be edited or submitted
                                        again.
                                    </Alert>

                                    <Button
                                        type="submit"
                                        color="red"
                                        size="md"
                                        loading={submitting}
                                        leftSection={
                                            <IconSend size={17} />
                                        }
                                    >
                                        Submit Allowance Request
                                    </Button>
                                </Stack>
                            </form>
                        </Paper>
                    )}

                    {pageState === "submitted" && (
                        <Paper
                            withBorder
                            radius="md"
                            p="md"
                        >
                            <Stack gap="md">
                                <Group
                                    justify="space-between"
                                    align="flex-start"
                                >
                                    <Group gap="sm">
                                        <ThemeIcon
                                            color="green"
                                            variant="light"
                                            size="lg"
                                            radius="xl"
                                        >
                                            <IconCheck size={20} />
                                        </ThemeIcon>

                                        <Box>
                                            <Text fw={700}>
                                                Request submitted
                                            </Text>

                                            <Text
                                                size="sm"
                                                c="dimmed"
                                            >
                                                Your allowance request
                                                has already been received.
                                            </Text>
                                        </Box>
                                    </Group>

                                    {renderStatus()}
                                </Group>

                                <Text size="sm" c="dimmed">
                                    Submitted:{" "}
                                    {formatDate(
                                        existing?.createdAt
                                    )}
                                </Text>

                                <Divider />

                                <Text size="sm">
                                    To view your submitted details,
                                    enter the email address you used
                                    when submitting the request.
                                </Text>

                                <form onSubmit={viewDetails}>
                                    <Stack gap="sm">
                                        <TextInput
                                            label="Email address"
                                            placeholder="name@example.com"
                                            type="email"
                                            value={email}
                                            onChange={(event) => {
                                                setEmail(
                                                    event.currentTarget.value
                                                );
                                                setEmailError("");
                                            }}
                                            error={emailError}
                                            required
                                            disabled={checkingEmail}
                                        />

                                        <Button
                                            type="submit"
                                            color="red"
                                            loading={checkingEmail}
                                            leftSection={
                                                <IconEye size={17} />
                                            }
                                        >
                                            View my allowance details
                                        </Button>
                                    </Stack>
                                </form>
                            </Stack>
                        </Paper>
                    )}

                    {pageState === "details" && details && (
                        <Paper
                            withBorder
                            radius="md"
                            p="md"
                        >
                            <Stack gap="md">
                                <Group
                                    justify="space-between"
                                    align="flex-start"
                                >
                                    <Box>
                                        <Title order={4}>
                                            Submitted allowance request
                                        </Title>

                                        <Text
                                            size="sm"
                                            c="dimmed"
                                            mt={4}
                                        >
                                            Submitted{" "}
                                            {formatDate(
                                                details.createdAt
                                            )}
                                        </Text>
                                    </Box>

                                    {renderStatus()}
                                </Group>

                                <Alert
                                    color="green"
                                    variant="light"
                                    icon={
                                        <IconCheck size={18} />
                                    }
                                >
                                    Your request has been received.
                                    The information below is read-only.
                                </Alert>

                                <Divider
                                    label="Beneficiary information"
                                    labelPosition="left"
                                />

                                {renderDetailsRow(
                                    "Beneficiary name",
                                    details.beneficiaryName
                                )}

                                {renderDetailsRow(
                                    "Beneficiary address",
                                    details.beneficiaryAddress
                                )}

                                {renderDetailsRow(
                                    "Email address",
                                    details.email
                                )}

                                <Divider
                                    label="Bank information"
                                    labelPosition="left"
                                />

                                {renderDetailsRow(
                                    "Bank name",
                                    details.bankName
                                )}

                                {renderDetailsRow(
                                    "Bank address",
                                    details.bankAddress
                                )}

                                {renderDetailsRow(
                                    "Account number",
                                    details.accountNumber
                                )}

                                {renderDetailsRow(
                                    "IBAN",
                                    details.iban
                                )}

                                {renderDetailsRow(
                                    "BIC / SWIFT",
                                    details.bicSwiftAddress
                                )}

                                <Divider
                                    label="Additional information"
                                    labelPosition="left"
                                />

                                {renderDetailsRow(
                                    "Comment",
                                    details.comment
                                )}

                                {details.paidAt && (
                                    <>
                                        <Divider />

                                        {renderDetailsRow(
                                            "Paid at",
                                            formatDate(
                                                details.paidAt
                                            )
                                        )}
                                    </>
                                )}

                                <Button
                                    variant="light"
                                    color="gray"
                                    leftSection={
                                        <IconLock size={16} />
                                    }
                                    onClick={() => {
                                        setDetails(null);
                                        setEmail("");
                                        setPageState("submitted");
                                    }}
                                >
                                    Hide details
                                </Button>
                            </Stack>
                        </Paper>
                    )}
                </Stack>
            </Accordion.Panel>
        </Accordion.Item>
    );
}
